import { memo, useCallback, useEffect, useState } from 'react';
import { Handle, Position } from 'reactflow';
import {
  QrCode, X, Loader2, CheckCircle, AlertCircle, Play,
  Download, Eye, Link as LinkIcon, Palette, Circle, Type, Image as ImageIcon,
  Settings2, Square, CircleDot, Grip, Rows3, Columns3,
} from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

// Classic B&W defaults so a first-time user gets a scannable QR with
// zero configuration.
const DEFAULT_MODULE_COLOR = '#000000';
const DEFAULT_EYE_COLOR = '#000000';
const DEFAULT_CAPTION_COLOR = '#000000';
const DEFAULT_BG_COLOR = '#FFFFFF';
const DEFAULT_SHAPE = 'square';

const SHAPE_OPTIONS = [
  { id: 'square',   label: 'Cuadrado',  icon: Square,     hint: 'Clásico' },
  { id: 'rounded',  label: 'Redondo',   icon: Grip,       hint: 'Esquinas suaves' },
  { id: 'dots',     label: 'Puntos',    icon: CircleDot,  hint: 'Círculos separados' },
  { id: 'bars-v',   label: 'Barras V',  icon: Rows3,      hint: 'Rayas verticales' },
  { id: 'bars-h',   label: 'Barras H',  icon: Columns3,   hint: 'Rayas horizontales' },
];

const GenQRNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [url, setUrl] = useState(data.qrUrl ?? '');
  const [caption, setCaption] = useState(data.qrCaption ?? '');
  const [moduleColor, setModuleColor] = useState(data.qrModuleColor ?? DEFAULT_MODULE_COLOR);
  const [eyeColor, setEyeColor] = useState(data.qrEyeColor ?? DEFAULT_EYE_COLOR);
  const [captionColor, setCaptionColor] = useState(data.qrCaptionColor ?? DEFAULT_CAPTION_COLOR);
  const [bgTransparent, setBgTransparent] = useState(data.qrBgTransparent ?? false);
  const [bgColor, setBgColor] = useState(data.qrBgColor ?? DEFAULT_BG_COLOR);
  const [moduleShape, setModuleShape] = useState(data.qrModuleShape ?? DEFAULT_SHAPE);
  // When true, force the form UI even if we already have an output — used
  // by the "Reconfigurar" button so users can tweak params without losing
  // the current preview until they click Crear again.
  const [reconfigure, setReconfigure] = useState(false);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const getInputForNode = useWorkflowStore((state) => state.getInputForNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);

  const {
    processNode,
    status: execStatus,
    error: execError,
    reset: resetExec,
    insufficientCredits,
  } = useNodeExecution();
  const { openImagePreview } = useMediaPreview();

  const isProcessing =
    execStatus === 'uploading' ||
    execStatus === 'queued' ||
    execStatus === 'processing';

  const hasInput = edges.some((edge) => edge.target === id);
  const inputData = getInputForNode(id);

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const persist = useCallback((patch) => updateNodeData(id, patch), [id, updateNodeData]);

  const handleProcess = useCallback(async () => {
    const trimmed = (url || '').trim();
    if (!trimmed) return false;
    resetExec();

    const input = getInputForNode(id);
    const logoUrl =
      input?.outputUrl ||
      (typeof input?.preview === 'string' && /^https?:\/\//.test(input.preview) ? input.preview : null) ||
      null;

    const params = {
      url: trimmed,
      module_color: moduleColor || DEFAULT_MODULE_COLOR,
      eye_color: eyeColor || moduleColor || DEFAULT_MODULE_COLOR,
      module_shape: moduleShape || DEFAULT_SHAPE,
      transparent_background: !!bgTransparent,
    };
    if (!bgTransparent) params.background_color = bgColor || DEFAULT_BG_COLOR;
    if (caption && caption.trim()) {
      params.caption_text = caption.trim();
      params.caption_color = captionColor || DEFAULT_CAPTION_COLOR;
    }
    if (logoUrl) params.logo_url = logoUrl;

    const result = await processNode({ resultType: 'gen_qr', params });

    if (result?.outputUrl) {
      const filename = `qr_${Date.now()}.png`;
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        fileName: filename,
        processedAt: new Date().toISOString(),
        qrUrl: trimmed,
        qrCaption: caption,
        qrCaptionColor: captionColor,
        qrModuleColor: moduleColor,
        qrEyeColor: eyeColor,
        qrBgTransparent: bgTransparent,
        qrBgColor: bgColor,
        qrModuleShape: moduleShape,
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'image',
        tool: 'genQR',
        filename,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      setReconfigure(false);
      return true;
    }
    return false;
  }, [
    id, url, caption, captionColor, moduleColor, eyeColor,
    bgTransparent, bgColor, moduleShape,
    getInputForNode, processNode, resetExec, updateNodeData, addAsset,
  ]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = (url || '').trim().length > 0 && !isProcessing;
  const showForm = !data.outputUrl || reconfigure;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(url, 'escribir el enlace o el texto del QR')]);

  return (
    <div
      className={`node-container genQR-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}
      data-testid={`gen-qr-node-${id}`}
    >
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Optional logo input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="node-handle input-handle"
      />

      {/* Header */}
      <div className="node-header genQR-header">
        <div className="node-header-left">
          <QrCode size={16} />
          <span>Generador QR</span>
        </div>
        <button
          className="node-delete-btn"
          onClick={() => deleteNode(id)}
          data-testid={`gen-qr-delete-${id}`}
        >
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content">
        {/* Logo input status */}
        <div className={`node-input-status ${hasInput && inputData?.outputUrl ? 'connected' : 'disconnected'}`}>
          {hasInput && inputData?.outputUrl ? (
            <>
              <CheckCircle size={12} />
              <span>Logo conectado</span>
            </>
          ) : (
            <>
              <ImageIcon size={12} />
              <span>Sin logo (opcional)</span>
            </>
          )}
        </div>

        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>{execStatus === 'queued' ? 'En cola...' : 'Generando QR...'}</span>
            </div>
          </div>
        ) : showForm ? (
          <div className="node-action-area genQR-form">
            {/* Small preview at the top if we're re-configuring an existing QR */}
            {data.outputUrl && reconfigure && (
              <div className="genQR-reconfig-preview">
                <img src={data.outputUrl} alt="QR actual" />
                <span className="genQR-reconfig-hint">QR actual — cambiá parámetros y volvé a crear</span>
              </div>
            )}

            <label className="genQR-field">
              <span className="genQR-field-label">
                <LinkIcon size={12} /> URL a codificar
              </span>
              <input
                type="text"
                value={url}
                onChange={(e) => { setUrl(e.target.value); persist({ qrUrl: e.target.value }); }}
                placeholder="https://tu-sitio.com"
                className="node-prompt-input"
                data-testid={`gen-qr-url-${id}`}
              />
            </label>

            {/* Module shape selector */}
            <div className="genQR-field">
              <span className="genQR-field-label">
                <Palette size={12} /> Estilo de los puntos
              </span>
              <div className="genQR-shape-picker" role="radiogroup" aria-label="Estilo de los módulos">
                {SHAPE_OPTIONS.map(({ id: sid, label, icon: Icon, hint }) => (
                  <button
                    key={sid}
                    type="button"
                    role="radio"
                    aria-checked={moduleShape === sid}
                    title={`${label} — ${hint}`}
                    className={`genQR-shape-btn ${moduleShape === sid ? 'active' : ''}`}
                    onClick={() => { setModuleShape(sid); persist({ qrModuleShape: sid }); }}
                    data-testid={`gen-qr-shape-${sid}-${id}`}
                  >
                    <Icon size={14} />
                    <span className="genQR-shape-label">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Colors row */}
            <div className="genQR-colors-row">
              <label className="genQR-color-field" title="Color de los módulos (dots)">
                <span className="genQR-field-label">
                  <Palette size={12} /> Módulos
                </span>
                <input
                  type="color"
                  value={moduleColor}
                  onChange={(e) => { setModuleColor(e.target.value); persist({ qrModuleColor: e.target.value }); }}
                  data-testid={`gen-qr-module-color-${id}`}
                />
              </label>
              <label className="genQR-color-field" title="Color de los ojos (finder patterns)">
                <span className="genQR-field-label">
                  <Circle size={12} /> Ojos
                </span>
                <input
                  type="color"
                  value={eyeColor}
                  onChange={(e) => { setEyeColor(e.target.value); persist({ qrEyeColor: e.target.value }); }}
                  data-testid={`gen-qr-eye-color-${id}`}
                />
              </label>
            </div>

            {/* Background */}
            <div className="genQR-bg-row">
              <label className="genQR-bg-toggle">
                <input
                  type="checkbox"
                  checked={bgTransparent}
                  onChange={(e) => { setBgTransparent(e.target.checked); persist({ qrBgTransparent: e.target.checked }); }}
                  data-testid={`gen-qr-bg-transparent-${id}`}
                />
                <span>Fondo transparente</span>
              </label>
              {!bgTransparent && (
                <label className="genQR-color-field genQR-color-inline" title="Color de fondo">
                  <span className="genQR-field-label">Fondo</span>
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => { setBgColor(e.target.value); persist({ qrBgColor: e.target.value }); }}
                    data-testid={`gen-qr-bg-color-${id}`}
                  />
                </label>
              )}
            </div>

            {/* Caption + caption color */}
            <label className="genQR-field">
              <span className="genQR-field-label">
                <Type size={12} /> Texto debajo (opcional)
              </span>
              <div className="genQR-caption-row">
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => { setCaption(e.target.value); persist({ qrCaption: e.target.value }); }}
                  placeholder="Escaneá para más info"
                  className="node-prompt-input"
                  data-testid={`gen-qr-caption-${id}`}
                />
                <input
                  type="color"
                  value={captionColor}
                  onChange={(e) => { setCaptionColor(e.target.value); persist({ qrCaptionColor: e.target.value }); }}
                  title="Color del texto"
                  data-testid={`gen-qr-caption-color-${id}`}
                />
              </div>
            </label>

            {!canProcess && !url.trim() && (
              <div className="genQR-hint">
                <AlertCircle size={11} /> Ingresá una URL para habilitar la generación
              </div>
            )}

            <div className="genQR-form-actions">
              {data.outputUrl && reconfigure && (
                <button
                  className="node-mini-preview-btn"
                  onClick={() => setReconfigure(false)}
                  data-testid={`gen-qr-cancel-reconfig-${id}`}
                >
                  Cancelar
                </button>
              )}
              <button
                className="node-process-btn"
                onClick={handleProcess}
                disabled={!canProcess}
                title={requirementsTitle(runMissing)}
                data-testid={`gen-qr-create-${id}`}
              >
                <Play size={14} /> {data.outputUrl ? 'Regenerar con cambios' : 'Crear QR'}
              </button>
              <NodeRequirements missing={runMissing} />
            </div>
          </div>
        ) : (
          <div className="node-result">
            <div className="genQR-result-container">
              <img
                src={data.outputUrl}
                alt="QR generado"
                className="node-result-image genQR-result clickable"
                onClick={() => openImagePreview(data.outputUrl, 'QR generado')}
                data-testid={`gen-qr-preview-${id}`}
              />
            </div>
            <div className="node-result-actions">
              <button
                className="node-mini-preview-btn"
                onClick={() => openImagePreview(data.outputUrl, 'QR generado')}
              >
                <Eye size={12} /> Ver
              </button>
              <button
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, data.fileName || 'qr.png'); }}
                data-testid={`gen-qr-download-${id}`}
              >
                <Download size={12} /> PNG
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="genqr" testIdPrefix="gen-qr-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} /> <span>QR listo</span>
            </div>
            <button
              className="node-process-btn genQR-regen-btn"
              onClick={() => setReconfigure(true)}
              data-testid={`gen-qr-reconfig-${id}`}
              title="Volver al formulario para cambiar parámetros"
            >
              <Settings2 size={12} /> Volver a configurar
            </button>
          </div>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output handle — always connectable so users can wire preview/other
          nodes before generating. */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={true}
      />
    </div>
  );
});

GenQRNode.displayName = 'GenQRNode';

export default GenQRNode;
